import React from 'react'
export default function DieselQuotesPage() {
    const [quotes, setQuotes] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [selectedQuote, setSelectedQuote] = React.useState(null);
    const [showDetailModal, setShowDetailModal] = React.useState(false);

    const fetchQuotes = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch('/api/get_diesel_quotes.php?page=1&limit=100', {
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error('Failed to fetch quotes');
            }

            const data = await response.json();
            setQuotes(data.quotes || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    React.useEffect(() => {
        fetchQuotes();
    }, []);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('en-ZA', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleViewQuote = (quote) => {
        setSelectedQuote(quote);
        setShowDetailModal(true);
    };

    const getStatusBadge = (status) => {
        const statusStyles = {
            'pending': 'bg-yellow-100 text-yellow-800',
            'quoted': 'bg-blue-100 text-blue-800',
            'accepted': 'bg-green-100 text-green-800',
            'rejected': 'bg-red-100 text-red-800',
            'completed': 'bg-gray-100 text-gray-800'
        };
        const style = statusStyles[status] || 'bg-gray-100 text-gray-800';
        return <span className={`px-2 py-1 text-xs font-medium rounded-full ${style}`}>{status}</span>;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <i className="fas fa-spinner fa-spin text-4xl text-blue-600 mb-4"></i>
                    <p className="text-gray-600">Loading diesel quotes...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Diesel Quotes</h1>
                    <p className="text-gray-600">List of all diesel quote requests with timestamps</p>
                </div>

                {/* Error */}
                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
                        <i className="fas fa-exclamation-triangle mr-2"></i>
                        {error}
                    </div>
                )}

                {/* Desktop Table View */}
                <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact Person</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact Number</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {quotes.length === 0 ? (
                                    <tr>
                                        <td colSpan="11" className="px-6 py-12 text-center text-gray-500">
                                            No diesel quotes found
                                        </td>
                                    </tr>
                                ) : (
                                    quotes.map((quote) => (
                                        <tr key={quote.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                #{quote.id}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {quote.product_name}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {quote.company_name}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {quote.delivery_location}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {quote.quantity_required}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {quote.contact_person}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {quote.contact_number}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {quote.email_address}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {getStatusBadge(quote.status)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {formatDate(quote.created_at)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                                                <button
                                                    onClick={() => handleViewQuote(quote)}
                                                    className="inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                                >
                                                    <i className="fas fa-eye mr-1"></i>
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden space-y-4">
                    {quotes.length === 0 ? (
                        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
                            No diesel quotes found
                        </div>
                    ) : (
                        quotes.map((quote) => (
                            <div key={quote.id} className="bg-white rounded-lg shadow p-4 space-y-3">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <p className="text-sm font-medium text-gray-900">#{quote.id}</p>
                                        <p className="text-sm text-gray-600">{quote.product_name}</p>
                                    </div>
                                    {getStatusBadge(quote.status)}
                                </div>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Company:</span>
                                        <span className="text-gray-900 text-right ml-4">{quote.company_name}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Location:</span>
                                        <span className="text-gray-900 text-right ml-4">{quote.delivery_location}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Quantity:</span>
                                        <span className="text-gray-900 text-right ml-4">{quote.quantity_required}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Contact:</span>
                                        <span className="text-gray-900 text-right ml-4">{quote.contact_person}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Phone:</span>
                                        <span className="text-gray-900 text-right ml-4">{quote.contact_number}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Email:</span>
                                        <span className="text-gray-900 text-right ml-4 break-all">{quote.email_address}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Created:</span>
                                        <span className="text-gray-900 text-right ml-4">{formatDate(quote.created_at)}</span>
                                    </div>
                                </div>
                                <div className="pt-3 border-t border-gray-100">
                                    <button
                                        onClick={() => handleViewQuote(quote)}
                                        className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                    >
                                        <i className="fas fa-eye mr-2"></i>
                                        View Details
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Quote Detail Modal */}
                {showDetailModal && selectedQuote && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center rounded-t-xl">
                                <h2 className="text-xl font-bold text-gray-900">Diesel Quote Details</h2>
                                <button
                                    onClick={() => setShowDetailModal(false)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <i className="fas fa-times text-xl"></i>
                                </button>
                            </div>
                            <div className="p-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-500 mb-1">Quote ID</label>
                                            <p className="text-sm text-gray-900 font-medium">#{selectedQuote.id}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-500 mb-1">Product</label>
                                            <p className="text-sm text-gray-900">{selectedQuote.product_name}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-500 mb-1">Listing Number</label>
                                            <p className="text-sm text-gray-900">{selectedQuote.listing_number || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-500 mb-1">Company Name</label>
                                            <p className="text-sm text-gray-900">{selectedQuote.company_name}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-500 mb-1">Delivery Location</label>
                                            <p className="text-sm text-gray-900">{selectedQuote.delivery_location}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-500 mb-1">Quantity Required</label>
                                            <p className="text-sm text-gray-900">{selectedQuote.quantity_required}</p>
                                        </div>
                                    </div>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-500 mb-1">Contact Person</label>
                                            <p className="text-sm text-gray-900">{selectedQuote.contact_person}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-500 mb-1">Contact Number</label>
                                            <p className="text-sm text-gray-900">{selectedQuote.contact_number}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-500 mb-1">Email Address</label>
                                            <p className="text-sm text-gray-900">{selectedQuote.email_address}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-500 mb-1">Status</label>
                                            <div className="mt-1">
                                                {getStatusBadge(selectedQuote.status)}
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-500 mb-1">Created At</label>
                                            <p className="text-sm text-gray-900">{formatDate(selectedQuote.created_at)}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-500 mb-1">Last Updated</label>
                                            <p className="text-sm text-gray-900">{formatDate(selectedQuote.updated_at)}</p>
                                        </div>
                                    </div>
                                </div>
                                {selectedQuote.admin_notes && (
                                    <div className="mt-6 pt-6 border-t border-gray-200">
                                        <label className="block text-sm font-medium text-gray-500 mb-2">Admin Notes</label>
                                        <div className="bg-gray-50 rounded-lg p-4">
                                            <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedQuote.admin_notes}</p>
                                        </div>
                                    </div>
                                )}
                                <div className="mt-6 pt-6 border-t border-gray-200 flex justify-end">
                                    <button
                                        onClick={() => setShowDetailModal(false)}
                                        className="bg-gray-200 text-gray-800 py-2 px-6 rounded-lg hover:bg-gray-300 transition-colors"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
